import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Search, PenTool, Droplets, TreeDeciduous, Map, HandHelping,
  ArrowRight, CheckCircle2, RefreshCw, Leaf, Send
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { PermacultureZonesDial } from '../components/interactive/PermacultureZonesDial';
import { TiltCard } from '../components/effects/TiltCard';
import { RippleButton } from '../components/effects/RippleButton';
import { ParallaxHero } from '../components/effects/ParallaxHero';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// South India specific images - optimized for web
const HERO_IMAGE = "https://images.unsplash.com/photo-1652722736772-c48164a98c7a?w=1600&q=75&fm=webp";
const WATER_IMAGE = "https://images.unsplash.com/photo-1729678426987-443dcb81fd2f?w=800&q=75&fm=webp";
const FOREST_IMAGE = "https://images.unsplash.com/photo-1615201124797-00000d2ea718?w=800&q=75&fm=webp";
const WHOLE_SYSTEMS_IMAGE = "https://images.unsplash.com/photo-1597474417024-3ca3baa9fb13?w=800&q=75&fm=webp";
const DESIGN_REFERENCE_IMAGE = "https://customer-assets.emergentagent.com/job_38ca16b8-b881-4255-a6f4-b41a6f37c1b3/artifacts/2yhu7tic_WhatsApp%20Image%202026-03-06%20at%202.27.28%E2%80%AFPM.jpeg";

const iconMap = {
  "Search": Search,
  "PenTool": PenTool,
  "Droplets": Droplets,
  "TreeDeciduous": TreeDeciduous,
  "Map": Map,
  "HandHelping": HandHelping,
  "RefreshCw": RefreshCw,
  "Leaf": Leaf,
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Service categories based on the provided content
const serviceCategories = [
  {
    title: "Water",
    subtitle: "Nature's Pulse",
    description: "Understanding how water moves through the seasons is critical when devising ways to harness it. Lo-tek methods are all centered around water.",
    longDescription: "Passive irrigation for plants and animals, groundwater percolation for aquifers, aquaculture, water for drinking, cleaning, power generation, drought/fire prevention can be designed into any habitat to reduce dependencies on elaborate water systems.",
    image: WATER_IMAGE,
    features: [
      { name: "Passive irrigation", desc: "Design systems for plants and animals" },
      { name: "Groundwater percolation", desc: "Recharge aquifers naturally" },
      { name: "Aquaculture", desc: "Water bodies for multiple purposes" },
      { name: "Drought/fire prevention", desc: "Build resilience into the landscape" }
    ]
  },
  {
    title: "Forests",
    subtitle: "Building with Nature",
    description: "With water we build soil, and direct fire and wind energies with flora groupings leveraging insects and fauna as invisible powerhouses in the process.",
    longDescription: "Applying a modular framework we seek to increase multi-functionality within the site with respect to the five elements. A keen ear and a sharp eye deliver outstanding attention to detail.",
    image: FOREST_IMAGE,
    features: [
      { name: "Soil building", desc: "Through strategic water management" },
      { name: "Flora groupings", desc: "Direct fire and wind energies" },
      { name: "Multi-functionality", desc: "Maximize site potential" },
      { name: "Five elements framework", desc: "Holistic design approach" }
    ]
  },
  {
    title: "Whole Systems",
    subtitle: "Integrated Approach",
    description: "Once the physical and ecological integrity of the watershed is stabilized, we help in designing and implementing whole systems for the commons, farms and organizations.",
    longDescription: "By tailoring an ONSITE PLANNING MEETING to fit your circumstances and what we see you need and no more, we want you to use us to get started and take over from there.",
    image: WHOLE_SYSTEMS_IMAGE,
    features: [
      { name: "Watershed stabilization", desc: "Foundation for whole system health" },
      { name: "Commons design", desc: "For communities and organizations" },
      { name: "Onsite planning", desc: "Custom tailored to your needs" },
      { name: "Knowledge transfer", desc: "Empowering you to take over" }
    ]
  }
];

export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Contact form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    land_size: '',
    message: ''
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError(null);

    try {
      await axios.post(`${API}/contact`, formData);
      setFormSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        location: '',
        land_size: '',
        message: ''
      });
    } catch (err) {
      setFormError('Failed to submit. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <main className="min-h-screen" data-testid="services-page">
      {/* Hero Section with Parallax */}
      <ParallaxHero
        imageSrc={HERO_IMAGE}
        imageAlt="Coffee plantation landscape Karnataka"
        className="h-[60vh] min-h-[500px] flex items-center justify-center"
        overlayClassName="bg-forest/70"
        parallaxIntensity={0.2}
      >
        <div className="flex items-center justify-center h-full">
          <div className="text-center text-white px-6 max-w-4xl mx-auto">
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-sm tracking-[0.3em] uppercase mb-6 text-white/80"
            >
              Regenerative Design and Planning
            </motion.p>
            
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="font-serif text-4xl sm:text-5xl md:text-6xl leading-tight mb-8"
              data-testid="services-headline"
            >
              Design Services
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-lg text-white/90 max-w-2xl mx-auto leading-relaxed"
            >
              Are you looking to save hours in guesswork and lakhs in implementing costs?
            </motion.p>
          </div>
        </div>
      </ParallaxHero>

      {/* Design Services Intro */}
      <section className="section-padding bg-cream" data-testid="design-intro">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <p className="text-sm tracking-[0.2em] uppercase text-earth mb-4">Our Approach</p>
              <h2 className="font-serif text-3xl md:text-4xl text-forest mb-6">
                Recreating Tropical Forest Landscapes
              </h2>
              <p className="text-foreground/70 leading-relaxed mb-6">
                We design plans for homesteads, small communities and citizen restoration projects. 
                Using well tested methods in agro-ecology, we recreate tropical forest landscapes using 
                nature as our master guide.
              </p>
              <p className="text-foreground/70 leading-relaxed mb-6">
                Along with observation, maps are one of the core tools we use to set in place FLR, ANR 
                and FMNR strategies.
              </p>
              <div className="bg-sand/50 rounded-xl p-6 border-l-4 border-earth">
                <p className="text-foreground/80 italic leading-relaxed">
                  The plans that we devise are simple and easy to follow — using "cut to the chase" reports — 
                  so you can select trajectory that suits your budget and aspirations and act on it immediately.
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img 
                src={DESIGN_REFERENCE_IMAGE}
                alt="GIS mapping and landscape design outputs"
                className="rounded-2xl shadow-lg w-full"
                loading="lazy"
              />
            </motion.div>
          </div>
          
          {/* Philosophy Statement */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="bg-forest text-white rounded-2xl p-8 md:p-12 text-center max-w-4xl mx-auto"
          >
            <p className="text-white/90 text-lg leading-relaxed">
              We are driven by systems thinking processes to teach people about their land along with 
              the community at every stage. Through long term engagement we seek to empower people 
              with the know-how they need to be comfortable in the systems we create for them.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Service Categories - Water, Forests, Whole Systems */}
      <section className="section-padding bg-sand/30" data-testid="service-categories">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-3xl md:text-4xl text-forest mb-4">
              Core Design Pillars
            </h2>
            <p className="text-foreground/70 max-w-2xl mx-auto">
              Our approach integrates three fundamental elements of landscape restoration.
            </p>
          </motion.div>
          
          <div className="space-y-24">
            {serviceCategories.map((category, index) => (
              <motion.div
                key={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={staggerContainer}
                className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
              >
                <motion.div 
                  variants={fadeInUp}
                  className={index % 2 === 1 ? 'lg:order-2' : ''}
                >
                  <div className="relative">
                    <img 
                      src={category.image}
                      alt={category.title}
                      className="rounded-2xl shadow-lg w-full h-80 object-cover"
                      loading="lazy"
                    />
                    <div className="absolute -bottom-6 -right-6 bg-forest text-white px-6 py-3 rounded-xl font-serif text-xl">
                      {category.title}
                    </div>
                  </div>
                </motion.div>
                
                <motion.div 
                  variants={fadeInUp}
                  className={index % 2 === 1 ? 'lg:order-1' : ''}
                >
                  <p className="text-sm tracking-[0.2em] uppercase text-earth mb-2">{category.subtitle}</p>
                  <h3 className="font-serif text-2xl md:text-3xl text-forest mb-4">{category.title}</h3>
                  <p className="text-foreground/70 leading-relaxed mb-4">{category.description}</p>
                  <p className="text-foreground/70 leading-relaxed mb-6">{category.longDescription}</p>
                  
                  <div className="grid sm:grid-cols-2 gap-3">
                    {category.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-3 bg-white rounded-xl p-4 shadow-sm">
                        <CheckCircle2 className="w-5 h-5 text-forest flex-shrink-0 mt-0.5" />
                        <div>
                          <span className="text-forest font-medium text-sm block">{feature.name}</span>
                          <span className="text-foreground/60 text-xs">{feature.desc}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Packages and Pricing */}
      <section className="section-padding bg-cream" data-testid="pricing-section">
        <div className="container-custom">
          {/* Pricing Grid */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="mb-16"
          >
            <h2 className="font-serif text-3xl md:text-4xl text-forest text-center mb-4">
              Service Packages and Pricing
            </h2>
            <p className="text-center text-foreground/70 max-w-2xl mx-auto mb-12">
              Transparent pricing for all our ecological design services. Custom packages available for larger projects.
            </p>
          </motion.div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-4 border-forest border-t-transparent rounded-full spinner" />
            </div>
          ) : (
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
            >
              {services.map((service) => {
                const IconComponent = iconMap[service.icon] || Search;
                
                return (
                  <TiltCard key={service.id} intensity={8}>
                    <motion.div
                      variants={fadeInUp}
                      className="bg-white border border-border/40 rounded-2xl p-6 group flex flex-col h-full"
                      data-testid={`service-${service.id}`}
                    >
                      <div className="icon-bg mb-4 group-hover:bg-forest/20 w-12 h-12">
                        <IconComponent className="w-6 h-6 text-forest" />
                      </div>
                      <h3 className="font-serif text-lg text-forest mb-2 min-h-[56px]">{service.title}</h3>
                      
                      {/* Price always at same position */}
                      <div className="h-8 mb-3">
                        {service.price && (
                          <span className="text-earth font-semibold text-sm">{service.price}</span>
                        )}
                      </div>
                      
                      <p className="text-foreground/70 text-sm leading-relaxed mb-4 flex-grow">
                        {service.description}
                      </p>
                      
                      <ul className="space-y-1.5 mt-auto">
                        {service.features.slice(0, 3).map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-xs">
                            <CheckCircle2 className="w-3.5 h-3.5 text-forest flex-shrink-0 mt-0.5" />
                            <span className="text-foreground/60">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </motion.div>
                  </TiltCard>
                );
              })}
            </motion.div>
          )}
        </div>
      </section>

      {/* Permaculture Zones Section */}
      <section className="section-padding bg-sand" data-testid="zones-section">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <p className="text-sm tracking-[0.2em] uppercase text-earth mb-4">Understanding Land Zones</p>
            <h2 className="font-serif text-3xl md:text-4xl text-forest mb-4">
              Permaculture Zones
            </h2>
            <p className="text-foreground/70 max-w-2xl mx-auto">
              Permaculture organizes land into zones based on frequency of use and attention needed. 
              Click on the zones to explore each one.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <PermacultureZonesDial />
          </motion.div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-cream" data-testid="process-section">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-3xl md:text-4xl text-forest mb-4">
              How We Work
            </h2>
            <p className="text-foreground/70 max-w-2xl mx-auto">
              Our collaborative process ensures your land receives the attention and expertise it deserves.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Initial Consultation", desc: "Share your land details and goals through our contact form." },
              { step: "02", title: "Site Visit", desc: "We assess your land's water, soil, vegetation, and potential." },
              { step: "03", title: "Design Development", desc: "We create comprehensive plans using GIS and ecological principles." },
              { step: "04", title: "Implementation Support", desc: "Ongoing guidance as your landscape transforms." }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-5xl font-serif text-forest/20 mb-4">{item.step}</div>
                <h3 className="font-serif text-lg text-forest mb-2">{item.title}</h3>
                <p className="text-foreground/70 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="section-padding bg-forest" data-testid="services-contact-form">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="text-white"
            >
              <p className="text-sm tracking-[0.2em] uppercase text-white/60 mb-4">Get Started</p>
              <h2 className="font-serif text-3xl md:text-4xl mb-6">
                Ready to Transform<br />
                <span className="italic">Your Land?</span>
              </h2>
              <p className="text-white/80 leading-relaxed mb-8">
                Every landscape is unique. Let's discuss how our services can help restore 
                the ecological health of your land.
              </p>
              
              <div className="space-y-4 text-white/70">
                <p className="flex items-center gap-3">
                  <span className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center text-sm">1</span>
                  Share your land details
                </p>
                <p className="flex items-center gap-3">
                  <span className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center text-sm">2</span>
                  We'll review and respond within 48 hours
                </p>
                <p className="flex items-center gap-3">
                  <span className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center text-sm">3</span>
                  Schedule a site visit or consultation
                </p>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ delay: 0.2 }}
            >
              {formSuccess ? (
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center text-white">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Send className="w-8 h-8" />
                  </div>
                  <h3 className="font-serif text-2xl mb-4">Message Sent!</h3>
                  <p className="text-white/80">
                    We'll review your inquiry and get back to you within 48 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-white/10 backdrop-blur-sm rounded-2xl p-8" data-testid="services-form">
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-white/80 text-sm">Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Your name"
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-xl h-11"
                        data-testid="services-input-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-white/80 text-sm">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="your@email.com"
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-xl h-11"
                        data-testid="services-input-email"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div className="space-y-2">
                      <Label htmlFor="location" className="text-white/80 text-sm">Land Location</Label>
                      <Input
                        id="location"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        placeholder="District, State"
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-xl h-11"
                        data-testid="services-input-location"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="land_size" className="text-white/80 text-sm">Land Size</Label>
                      <Input
                        id="land_size"
                        name="land_size"
                        value={formData.land_size}
                        onChange={handleChange}
                        placeholder="e.g., 5 acres"
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-xl h-11"
                        data-testid="services-input-land-size"
                      />
                    </div>
                  </div>

                  <div className="space-y-2 mb-6">
                    <Label htmlFor="message" className="text-white/80 text-sm">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      placeholder="Tell us about your land and what you'd like to achieve..."
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-xl resize-none"
                      data-testid="services-input-message"
                    />
                  </div>

                  {formError && (
                    <div className="p-3 bg-clay/20 border border-clay/30 rounded-xl text-white text-sm mb-4">
                      {formError}
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={formLoading}
                    className="w-full bg-white text-forest hover:bg-white/90 rounded-full py-5 font-semibold text-sm tracking-wider uppercase"
                    data-testid="services-submit-btn"
                  >
                    {formLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-forest border-t-transparent rounded-full spinner mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Inquiry
                      </>
                    )}
                  </Button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}
